import { useId, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearch } from '@tanstack/react-router';
import { Paperclip } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDateTime, labelOf, OUTBOUND_SOURCE_LABELS } from '@blimo/shared';
import { Badge, DataTable, ErrorState, Input, Label, PageHeader, SearchInput, StatusBadge } from '@blimo/ui';
import { FilterBar, FilterSelect } from '../../../components/filter-bar.jsx';
import { useListState } from '../../../lib/list-search.js';
import { recipientText } from '../recipients.js';

const SOURCE_OPTIONS = Object.entries(OUTBOUND_SOURCE_LABELS).map(([value, label]) => ({ value, label }));
/** The list endpoint only filters these two (`filter[status]` in:sent,failed). */
const STATUS_OPTIONS = [
  { value: 'sent', label: 'Versendet' },
  { value: 'failed', label: 'Fehlgeschlagen' },
];

/** Kommunikation › Versandhistorie (F-507). */
export function OutboundListPage() {
  const api = useApi();
  const search = useSearch({ strict: false });
  const list = useListState(search);
  const params = {
    ...list.apiParams,
    sort: undefined,
    filter: { search: search.q, source: search.source, status: search.status, from: search.from, to: search.to },
  };
  const { data, error, isPending, isFetching, refetch } = useQuery(adminQueries.outboundMessages(api, params));
  const columns = useMemo(() => outboundColumns(), []);

  return (
    <>
      <PageHeader title="Versandhistorie" description="Alle versendeten E-Mails der Plattform und aus dem Altportal – Empfänger, Inhalt, erneuter Versand." />
      <FilterBar>
        <SearchInput label="Versand durchsuchen" placeholder="Betreff oder Empfänger …" value={search.q ?? ''} onValueChange={(v) => list.setFilter('q', v)} />
        <FilterSelect label="Quelle" value={search.source} onChange={(v) => list.setFilter('source', v)} options={SOURCE_OPTIONS} />
        <FilterSelect label="Status" value={search.status} onChange={(v) => list.setFilter('status', v)} options={STATUS_OPTIONS} />
        <DateFilter label="Von" value={search.from} onChange={(v) => list.setFilter('from', v)} />
        <DateFilter label="Bis" value={search.to} onChange={(v) => list.setFilter('to', v)} />
      </FilterBar>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <DataTable
          label="Versendete Nachrichten"
          columns={columns}
          data={data?.data}
          rowCount={data?.meta?.total}
          isLoading={isPending}
          isFetching={isFetching}
          getRowId={(row) => String(row.id)}
          pagination={list.pagination}
          onPaginationChange={list.onPaginationChange}
          emptyTitle="Keine Nachrichten gefunden"
        />
      )}
    </>
  );
}

function DateFilter({ label, value, onChange }) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input id={id} type="date" className="h-(--control-height-sm) sm:w-40" value={value ?? ''} onChange={(e) => onChange(e.target.value || undefined)} />
    </div>
  );
}

function outboundColumns() {
  return [
    { id: 'sent_at', header: 'Gesendet', cell: ({ row }) => <span className="numeric">{formatDateTime(row.original.sent_at)}</span> },
    {
      id: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          <Link
            to="/kommunikation/versandhistorie/$messageId"
            params={{ messageId: String(row.original.id) }}
            className="font-medium text-primary hover:underline"
          >
            {row.original.subject || '(ohne Betreff)'}
          </Link>
          {row.original.attachments?.length > 0 && <Paperclip className="size-3.5 text-muted-foreground" aria-label="mit Anhängen" />}
        </span>
      ),
    },
    { id: 'to', header: 'Empfänger', cell: ({ row }) => <span className="break-all text-sm">{recipientText(row.original.recipients)}</span> },
    { id: 'template', header: 'Vorlage', cell: ({ row }) => <span className="font-mono text-xs">{row.original.template_key ?? '–'}</span> },
    { id: 'source', header: 'Quelle', cell: ({ row }) => labelOf(OUTBOUND_SOURCE_LABELS, row.original.source) },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className="flex flex-wrap gap-1">
          <StatusBadge domain="outboundMessage" status={row.original.status} />
          {row.original.body_redacted && <Badge variant="neutral">Inhalt nicht gespeichert</Badge>}
        </span>
      ),
    },
  ];
}
